import React, { useState } from "react";
import BackButton from "../components/BackButton";

function PointSystem() {
  const [activeTab, setActiveTab] = useState("t20"); // 't20', 'odi', 'test'

  // Accordion State (Default 'batting' is open)
  const [openSection, setOpenSection] = useState("batting");

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  // --- DATA STRUCTURE ---
  const pointsData = {
    t20: {
      batting: [
        { event: "Run Scored", points: "+1" },
        { event: "Boundary Bonus (4)", points: "+1" },
        { event: "Six Bonus (6)", points: "+2" },
        { event: "30 Run Bonus", points: "+4" },
        { event: "Half-century Bonus", points: "+8" },
        { event: "Century Bonus", points: "+16" },
        { event: "Dismissal for Duck (0)", points: "-2" },
        { event: "Strike Rate > 170 (Min 10 balls)", points: "+6" },
        { event: "Strike Rate 150-170 (Min 10 balls)", points: "+4" },
        { event: "Strike Rate 130-150 (Min 10 balls)", points: "+2" },
        { event: "Strike Rate 50-70 (Min 10 balls)", points: "-2" },
        { event: "Strike Rate < 50 (Min 10 balls)", points: "-4" },
      ],
      bowling: [
        { event: "Wicket (Excl. Run Out)", points: "+25" },
        { event: "Bonus (LBW / Bowled)", points: "+8" },
        { event: "3 Wicket Haul", points: "+4" },
        { event: "4 Wicket Haul", points: "+8" },
        { event: "5 Wicket Haul", points: "+16" },
        { event: "Maiden Over", points: "+12" },
        { event: "Dot Ball", points: "+1" },
        { event: "Economy Rate < 5 (Min 2 overs)", points: "+6" },
        { event: "Economy Rate 5-5.99 (Min 2 overs)", points: "+4" },
        { event: "Economy Rate 6-6.99 (Min 2 overs)", points: "+2" },
        { event: "Economy Rate 10-11 (Min 2 overs)", points: "-2" },
        { event: "Economy Rate 11-12 (Min 2 overs)", points: "-4" },
        { event: "Economy Rate > 12 (Min 2 overs)", points: "-6" },
      ],
      fielding: [
        { event: "Catch", points: "+8" },
        { event: "3 Catch Bonus", points: "+4" },
        { event: "Stumping", points: "+12" },
        { event: "Run Out (Direct Hit)", points: "+12" },
        { event: "Run Out (Thrower)", points: "+8" },
        { event: "Run Out (Catcher)", points: "+4" },
      ],
      other: [
        { event: "Starting 11 (Playing 11)", points: "+4" },
      ]
    },
    odi: {
      batting: [
        { event: "Run Scored", points: "+1" },
        { event: "Boundary Bonus (4)", points: "+1" },
        { event: "Six Bonus (6)", points: "+2" },
        { event: "Half-century Bonus", points: "+4" },
        { event: "Century Bonus", points: "+8" },
        { event: "Duck (0)", points: "-3" },
      ],
      bowling: [
        { event: "Wicket", points: "+25" },
        { event: "LBW / Bowled", points: "+8" },
        { event: "4 Wicket Haul", points: "+4" },
        { event: "5 Wicket Haul", points: "+8" },
        { event: "Maiden Over", points: "+4" },
      ],
      fielding: [
        { event: "Catch", points: "+8" },
        { event: "3 Catch Bonus", points: "+4" },
        { event: "Stumping", points: "+12" },
        { event: "Run Out", points: "+6" },
      ],
      other: [
        { event: "Starting 11 (Playing 11)", points: "+4" },
      ]
    },
    test: {
      batting: [
        { event: "Run Scored", points: "+1" },
        { event: "Boundary Bonus", points: "+1" },
        { event: "Six Bonus", points: "+2" },
        { event: "Century Bonus", points: "+8" },
        { event: "Double Century", points: "+16" },
        { event: "Duck", points: "-4" },
      ],
      bowling: [
        { event: "Wicket", points: "+16" },
        { event: "5 Wicket Haul", points: "+8" },
        { event: "Maiden Over", points: "0" },
      ],
      fielding: [
        { event: "Catch", points: "+8" },
        { event: "Stumping", points: "+12" },
        { event: "Run Out", points: "+12" },
      ],
      other: [
        { event: "Starting 11 (Playing 11)", points: "+4" },
      ]
    }
  };

  // Helper to Render Accordion Card
  const renderAccordion = (key, title, icon, color, data) => {
    const isOpen = openSection === key;

    return (
      <div
        className="card border-0 shadow-sm mb-3 overflow-hidden"
        style={{ borderRadius: "16px", transition: "all 0.3s ease" }}
      >
        {/* Header (Clickable) */}
        <div
          className="card-header bg-white border-0 p-3 d-flex align-items-center justify-content-between"
          onClick={() => toggleSection(key)}
          style={{ cursor: "pointer" }}
        >
          <div className="d-flex align-items-center gap-3">
            {/* Icon Box */}
            <div
              className="d-flex align-items-center justify-content-center text-white shadow-sm"
              style={{
                width: "45px", height: "45px", borderRadius: "12px",
                background: `linear-gradient(135deg, ${color}, ${color}dd)` // Maroon dynamic colors
              }}
            >
              <i className={`bi ${icon} fs-5`}></i>
            </div>
            <h6 className="fw-bold mb-0 text-dark">{title}</h6>
          </div>

          {/* Arrow */}
          <i
            className="bi bi-chevron-down text-muted"
            style={{
              transition: "transform 0.3s ease",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)"
            }}
          ></i>
        </div>

        {/* Content (Collapsible) */}
        <div
          style={{
            maxHeight: isOpen ? "800px" : "0px", // Increased max-height for new large data
            overflow: "hidden",
            transition: "max-height 0.4s ease-in-out",
            background: "#fff"
          }}
        >
          <div className="p-3 pt-0">
            <div className="rounded-3 overflow-hidden border border-light">
              <table className="table table-borderless mb-0">
                <thead className="table-light small text-uppercase text-muted">
                  <tr>
                    <th className="ps-3 py-2 fw-normal">Event</th>
                    <th className="text-end pe-3 py-2 fw-normal">Points</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, idx) => (
                    <tr key={idx} className="border-top border-light">
                      <td className="ps-3 py-3 text-secondary" style={{ fontSize: "0.9rem" }}>{item.event}</td>
                      <td className="text-end pe-3 py-3">
                        <span className={`badge rounded-pill px-3 py-1 ${item.points.includes('-') ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'}`}>
                          {item.points}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6", }}>

      {/* === HERO HEADER IN MAROON THEME === */}
      <div
        className="pb-5 pt-4 px-3 position-relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)", // 🔥 Maroon Gradient
          borderBottomLeftRadius: "30px",
          borderBottomRightRadius: "30px",
          height: "200px",
          boxShadow: "0 10px 20px rgba(128,0,0,0.15)"
        }}
      >
          <div className="position-relative z-1">
            <BackButton to="/home" label="" className="text-white" />

            <div className="text-center" style={{marginTop: "-40px"}}>
              {/* ✨ 1. Glowing Icon Container */}
              <div className="position-relative d-inline-block mb-2">
                {/* Background Glow */}
                <div className="position-absolute top-50 start-50 translate-middle bg-warning rounded-circle"
                  style={{ width: '40px', height: '40px', filter: 'blur(20px)', opacity: 0.6 }}></div>

                {/* Icon */}
                <i className="bi bi-trophy-fill text-warning fs-1 position-relative z-1"
                  style={{ textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}></i>
              </div>

              {/* ✨ 2. Premium Gradient Title */}
              <h3 className="fw-bold text-white text-uppercase mb-1 fst-italic"
                style={{
                  letterSpacing: "1px",
                  textShadow: "0 2px 4px rgba(0,0,0,0.2)"
                }}>
                Point System
              </h3>

              {/* ✨ 3. Season Badge */}
              <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mt-1"
                style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)" }}>
                <span className="bg-success rounded-circle shadow-sm" style={{ width: '6px', height: '6px' }}></span>
                <small className="text-white fw-bold opacity-90" style={{ fontSize: "0.65rem", letterSpacing: "1px" }}>
                  OFFICIAL RULES 2026
                </small>
              </div>
            </div>

          </div>
      </div>

      <div className="container-fluid px-3 mt-3 px-md-4" >

        {/* === TAB SWITCHER (Maroon Island Style) === */}
        <div className="bg-white p-1 rounded-pill shadow-sm d-flex mb-4 mx-auto" style={{ maxWidth: '400px' }}>
          {['t20', 'odi', 'test'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`btn flex-fill rounded-pill fw-bold text-uppercase py-2 small shadow-none border-0`}
              style={{ 
                  transition: "all 0.3s ease", 
                  letterSpacing: "1px",
                  background: activeTab === tab ? "linear-gradient(135deg, #800000 0%, #4A0000 100%)" : "transparent",
                  color: activeTab === tab ? "white" : "#6c757d"
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* === ACCORDION SECTIONS === */}
        <div className="fade-in-up pb-4">

          {/* 1. Batting - Dark Maroon */}
          {renderAccordion("batting", "Batting Points", "bi-circle-fill", "#800000", pointsData[activeTab].batting)}

          {/* 2. Bowling - Lighter Maroon / Red */}
          {renderAccordion("bowling", "Bowling Points", "bi-bullseye", "#A00000", pointsData[activeTab].bowling)}

          {/* 3. Fielding - Premium Gold */}
          {renderAccordion("fielding", "Fielding Points", "bi-people-fill", "#d4af37", pointsData[activeTab].fielding)}

          {/* 4. Other - Deep Maroon (Added for Starting 11) */}
          {renderAccordion("other", "Other Points", "bi-star-fill", "#5A0000", pointsData[activeTab].other)}

          {/* === IMPACT PLAYER CARD (Premium Maroon/Red Tone) === */}
          <div className="card border-0 shadow-sm text-white mt-4 overflow-hidden position-relative"
            style={{ borderRadius: "16px", background: "linear-gradient(135deg, #A00000 0%, #6A0000 100%)" }}>

            {/* Gold Shine Effect */}
            <div className="position-absolute top-0 end-0 bg-warning opacity-50" style={{ width: '150px', height: '150px', filter: 'blur(60px)', borderRadius: '50%' }}></div>

            <div className="card-body p-4 position-relative z-1">
              <h6 className="fw-bold mb-4 text-warning d-flex align-items-center text-uppercase small ls-1">
                <i className="bi bi-star-fill me-2"></i> Key Multipliers
              </h6>

              <div className="row g-3">
                <div className="col-6">
                  <div className="p-3 rounded-4 text-center shadow-sm" style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)" }}>
                    <span className="badge bg-white text-danger mb-2 px-3 shadow-sm">CAPTAIN</span>
                    <h2 className="fw-bold mb-0">2x</h2>
                    <small className="text-white-50" style={{ fontSize: '10px' }}>POINTS</small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="p-3 rounded-4 text-center shadow-sm" style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)" }}>
                    <span className="badge bg-white text-primary mb-2 text-center px-3 text-wrap shadow-sm">VICE CAPTAIN</span>
                    <h2 className="fw-bold mb-0">1.5x</h2>
                    <small className="text-white-50" style={{ fontSize: '10px' }}>POINTS</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* === FOOTER NOTE === */}
          <div className="text-center mt-4 text-muted opacity-75">
            <small className="d-block"><i className="bi bi-info-circle me-1"></i> Points are updated after match completion.</small>
          </div>

        </div>

      </div>

      <style>{`
        .ls-1 { letter-spacing: 1px; }
        .fade-in-up { animation: fadeInUp 0.5s ease-out forwards; }
        @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default PointSystem;